import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Popover from "@cloudscape-design/components/popover"
import SpaceBetween from "@cloudscape-design/components/space-between"

/**
 * Props for the SDSRuleBadge component.
 */
interface SDSRuleBadgeProps {
  /**
   * Translation confidence score (0.0-1.0). Determines badge color.
   */
  confidence?: number | null
  /**
   * Original Vault DSL expression shown in the expandable detail.
   */
  sourceReference?: string | null
  /**
   * Rule name from SDS (e.g., "AE_001").
   */
  ruleName?: string | null
  /**
   * Brief explanation of the translation logic.
   */
  reasoning?: string | null
}

/**
 * Provenance badge for rules auto-translated from SDS Vault DSL. Green for high confidence (>=0.8),
 * amber for lower confidence. Clickable to expand and show the original source expression.
 */
export const SDSRuleBadge: React.FC<SDSRuleBadgeProps> = ({
  confidence,
  sourceReference,
  ruleName,
  reasoning,
}) => {
  const isHighConfidence = (confidence ?? 0) >= 0.8
  const label = isHighConfidence ? "From EDC" : "From EDC - verify"

  return (
    <Popover
      triggerType="custom"
      position="bottom"
      size="large"
      header={ruleName ?? "EDC rule"}
      content={
        <SpaceBetween size="xs">
          {confidence != null && (
            <Box variant="small">Confidence: {Math.round(confidence * 100)}%</Box>
          )}
          {reasoning && <Box variant="small">Reasoning: {reasoning}</Box>}
          {sourceReference && (
            <Box>
              <Box variant="small" color="text-label">
                Original EDC rule:
              </Box>
              <Box variant="pre">{sourceReference}</Box>
            </Box>
          )}
        </SpaceBetween>
      }
    >
      <Badge color={isHighConfidence ? "green" : "severity-medium"}>{label}</Badge>
    </Popover>
  )
}
